import React, { useState, useEffect } from 'react';
import { Send, Mail, CheckCircle, AlertTriangle, ArrowLeft, UserPlus } from 'lucide-react';
import { EnrollmentApi } from '@/features/enrollment';
import { useAuth } from '@/features/auth';
import { Campaign } from '@/features/campaigns';

interface SelfEnrollmentFormData {
  clientEmail: string;
  agentName: string;
}

interface ClientSelfEnrollmentWizardProps {
  selectedCampaign?: Campaign | null;
}

export default function ClientSelfEnrollmentWizard({ selectedCampaign }: ClientSelfEnrollmentWizardProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<SelfEnrollmentFormData>({
    clientEmail: '',
    agentName: user ? `${user.firstName} ${user.lastName}`.trim() : ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountCreated, setAccountCreated] = useState(false);

  // Update agent name when user data is available
  useEffect(() => {
    if (user) {
      const fullName = `${user.firstName} ${user.lastName}`.trim();
      setFormData(prev => ({
        ...prev,
        agentName: fullName || user.email
      }));
    }
  }, [user]);

  const handleInputChange = (field: keyof SelfEnrollmentFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const createUserAccountForClient = async (email: string) => {
    try {
      // ✅ Get the selected campaign ID from props
      const campaignId = selectedCampaign?.id;
      const campaignName = selectedCampaign?.name;
      
      
      if (!campaignId) {
        console.error('❌ ERROR: No campaignId available! selectedCampaign:', selectedCampaign);
        throw new Error('No campaign selected. Please select a campaign first.');
      }
      
      await EnrollmentApi.createUserAccountForReferral(email, campaignId);
      setAccountCreated(true);
      return { success: true, newAccount: true };
    } catch (error: any) {
      // If account already exists, that's fine - continue with enrollment
      if (error.message?.includes('already exists') || error.status === 409) {
        return { success: true, newAccount: false };
      }
      console.error(`Failed to create account for ${email}:`, error);
      return { success: false, newAccount: false };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.clientEmail || !formData.agentName) {
      setError('Please fill in all required fields');
      return;
    }

    // Check if campaign is selected
    if (!selectedCampaign) {
      setError('Please select a campaign first. Go to "Campaign Selection" tab, choose a campaign, then return to this tab.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setAccountCreated(false);

    try {      
      // Create user account for the client (like referral logic)
      const result = await createUserAccountForClient(formData.clientEmail);
      
      if (result.success) {
        setIsSuccess(true);
        setAccountCreated(result.newAccount);
        // Reset form but keep agent name
        setFormData(prev => ({
          ...prev,
          clientEmail: ''
        }));
      } else {
        setError('Failed to create user account. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to process request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendAnother = () => {
    setIsSuccess(false);
    setError(null);
    setAccountCreated(false);
  };

  if (isSuccess) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="card">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-accent-900 mb-2">Account Process Completed!</h2>
            <p className="text-accent-600 mb-6">
              {accountCreated 
                ? `A new user account has been created for ${formData.clientEmail}`
                : `Account already exists for ${formData.clientEmail}`
              }
            </p>
            
            {/* Account Creation Status */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center space-x-2 text-green-800 mb-2">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">
                  {accountCreated ? 'New Account Created' : 'Account Already Exists'}
                </span>
              </div>
              <p className="text-sm text-green-700">
                {accountCreated 
                  ? 'User account was created and login credentials have been sent to the client\'s email address.'
                  : 'An account already exists for this email address. The client can use their existing login credentials.'
                }
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                {accountCreated && <li>• Client will receive login credentials for their new account</li>}
                <li>• Client can log in to their account to access the system</li>
                <li>• Client can complete enrollment forms at their own pace</li>
                <li>• You'll be notified when the client completes any forms</li>
                <li>• Completed forms will appear in your pending applications</li>
              </ul>
            </div>

            <div className="flex justify-center space-x-4">
              <button
                onClick={handleSendAnother}
                className="btn-primary"
              >
                Send Another Email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="card">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-accent-900 mb-2">Self-Enrollment Form</h1>
          <p className="text-accent-600">
            Send the enrollment form to your client via email
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Agent Name */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Your Name (Agent) *
            </label>
            <input
              type="text"
              className="input-field"
              placeholder="Enter your name"
              value={formData.agentName}
              onChange={(e) => handleInputChange('agentName', e.target.value)}
              required
            />
            <p className="text-xs text-accent-500 mt-1">
              This will be used for account creation
            </p>
          </div>

          {/* Client Email */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Client Email Address *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
              <input
                type="email"
                className="input-field pl-10"
                placeholder="Enter client's email address"
                value={formData.clientEmail}
                onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                required
              />
            </div>
            <p className="text-xs text-accent-500 mt-1">
              A user account will be created for this email address if one doesn't exist
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-red-900 mb-1">Error</h4>
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Information Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 text-sm font-semibold">i</span>
              </div>
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">How it works</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• System automatically creates a user account if one doesn't exist</li>
                  <li>• Login credentials are sent to the client's email</li>
                  <li>• Client can log in to their account to access the system</li>
                  <li>• Client can complete enrollment forms at their convenience</li>
                  <li>• Progress is automatically saved as they fill out forms</li>
                  <li>• Completed forms appear in your pending applications section</li>
                  <li>• You'll be notified when the client completes any forms</li>
                  <li>• <strong>User account:</strong> Client gets login credentials to access the system</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={isLoading || !formData.clientEmail || !formData.agentName || !selectedCampaign}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating Account...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Create User Account</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}